import { IconButton, Tooltip } from '@civilgrid/ui/components';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useSnapshot } from 'valtio';
import {
  addObjectFile as apiAddObjectFile,
  deleteObjectFiles as apiDeleteObjectFiles,
  getStoredFileDownloadUrl,
} from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { MapObject, ObjectFile, Project } from '~/common/types';
import { usePendingDownload } from '~/hooks/usePendingDownload';
import { isPermittingLocalMap } from '~/pages/projectDetails/pdfGeneration/permittingMapConstants';
import { useCivilDrawStore } from '../../../civilDrawStoreContext';
import { fileIconClassMap, imageExts, videoExts } from './constants';
import { FileView } from './FileView';

interface DownloadAnalyticsContext {
  projectId: string;
  organizationId?: string;
  objectType?: MapObject['type'];
  mapId?: string;
}

/** Marks a failure from the browser-side Blob/DOM download step, distinct from the fetch itself. */
class ClientDownloadError extends Error {}

/** Saves an attachment that lives only in the page, on a local permitting map with no stored object to sign. */
export const downloadObjectFile = async (file: ObjectFile, analytics: DownloadAnalyticsContext) => {
  const { name, path } = file;
  let httpStatus: number | undefined;

  try {
    const response = await fetch(path);
    if (!response.ok) {
      httpStatus = response.status;
      throw new Error(`Request failed with status ${response.status}`);
    }
    const blob = await response.blob();
    let url: string | undefined;
    try {
      try {
        url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
      } catch (err) {
        throw new ClientDownloadError(err instanceof Error ? err.message : 'Client download failed');
      }

      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawFileDownloaded, {
        project_id: analytics.projectId,
        organization_id: analytics.organizationId,
        object_type: mapObjectTypeToCivildrawObjectType(analytics.objectType),
        ...(analytics.mapId && { map_id: analytics.mapId }),
      });
    } finally {
      // Yield a tick before revoking; some browsers cancel the download otherwise.
      const revokeUrl = url;
      if (revokeUrl) setTimeout(() => window.URL.revokeObjectURL(revokeUrl), 0);
    }
  } catch (err) {
    const reason = err instanceof ClientDownloadError ? 'client_download_error' : String(httpStatus ?? 'network_error');
    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawFileDownloadFailed, {
      project_id: analytics.projectId,
      organization_id: analytics.organizationId,
      object_type: mapObjectTypeToCivildrawObjectType(analytics.objectType),
      ...(analytics.mapId && { map_id: analytics.mapId }),
      reason,
    });
    notifyDownloadError();
  }
};

/** Hands a CivilDraw attachment to the browser's download manager through a signed attachment URL. */
export const streamObjectFile = async (file: ObjectFile, analytics: DownloadAnalyticsContext) => {
  const eventProps = {
    project_id: analytics.projectId,
    organization_id: analytics.organizationId,
    object_type: mapObjectTypeToCivildrawObjectType(analytics.objectType),
    ...(analytics.mapId && { map_id: analytics.mapId }),
  };

  let url: string;
  try {
    ({ url } = await getStoredFileDownloadUrl(analytics.projectId, 'civildraw-attachment', file.id));
  } catch (err) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawFileDownloadFailed, {
      ...eventProps,
      reason: getReasonForError(err),
    });
    notifyDownloadError();
    return;
  }

  // The URL is signed as an attachment, so navigating to it saves the file and leaves the page in place.
  window.location.href = url;
  captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawFileDownloaded, eventProps);
};

export const ObjAttachmentsContainer = () => {
  const store = useCivilDrawStore();
  const { projectId, currentMapId, currentObjectId, currentObject, project } = useSnapshot(store) as typeof store;
  const [hasProject, setHasProject] = useState(false);
  const { pendingId, track } = usePendingDownload();
  const [projectFiles, setProjectFiles] = useState<ObjectFile[]>([]);
  const iconSize = 20;
  const iconStrokeWidth = 0.5;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!currentObject) return;
    let data = Reflect.get(currentObject, 'files');
    if (!data) return;

    // sort files by descending order of uploadedAt
    const dataCopy = [...data];
    data = dataCopy.sort((a, b) => {
      if (DateTime.fromISO(a.uploadedAt) > DateTime.fromISO(b.uploadedAt)) return -1;
      else return 1;
    });
    const incomingFileIds = data.map((file) => file.id);
    const existingFileIds = projectFiles.map((file) => file.id);
    if (isEqual(incomingFileIds, existingFileIds)) return;
    setProjectFiles(data);
  }, [currentObject]);

  useEffect(() => {
    setHasProject(projectFiles.length > 0);
  }, [projectFiles]);

  const sendFile = async (uploadedFile: File) => {
    const extension = uploadedFile.name.split('.').pop();
    if (!extension || !currentMapId || !currentObjectId) return;

    if (isPermittingLocalMap(currentMapId)) {
      const safeName = uploadedFile.name.replace(/\s/g, '_');
      const newFile: ObjectFile = {
        id: nanoid(),
        path: URL.createObjectURL(uploadedFile),
        name: safeName,
        extension,
        type: uploadedFile.type || 'application/octet-stream',
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'local',
        notes: '',
      };
      const existing = store.currentObject?.files ?? [];
      await store.updateObject({ files: [...existing, newFile] });
      store.shouldRedraw = true;
      return;
    }

    const data = new FormData();

    data.append('file', uploadedFile);
    data.append('name', uploadedFile.name.replace(/\s/g, '_'));
    data.append('type', uploadedFile.type);
    data.append('extension', extension);

    try {
      return await apiAddObjectFile({
        projectId: projectId,
        mapId: currentMapId,
        objectId: currentObjectId,
        file: data,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onDownloadFile = (file: ObjectFile) => {
    const analyticsProject = project as unknown as Project;
    const analytics = {
      projectId: analyticsProject.id,
      organizationId: analyticsProject.organization || undefined,
      objectType: currentObject?.type,
      mapId: currentMapId || undefined,
    };
    if (isPermittingLocalMap(currentMapId ?? '')) return downloadObjectFile(file, analytics);
    return track(file.id, () => streamObjectFile(file, analytics));
  };

  const onDeleteFile = async ({ id }: { id: string }) => {
    if (!currentMapId || !currentObjectId) return;
    try {
      if (isPermittingLocalMap(currentMapId)) {
        const files = store.currentObject?.files ?? [];
        const removed = files.find((f) => f.id === id);
        if (removed?.path.startsWith('blob:')) {
          URL.revokeObjectURL(removed.path);
        }
        await store.updateObject({ files: files.filter((f) => f.id !== id) });
        store.shouldRedraw = true;
      } else {
        await apiDeleteObjectFiles({
          projectId,
          mapId: currentMapId,
          objectId: currentObjectId,
          files: [id],
        });
        store.refreshMap();
      }
      const analyticsProject = project as unknown as Project;
      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawFileDeleted, {
        project_id: analyticsProject.id,
        organization_id: analyticsProject.organization || undefined,
        object_type: mapObjectTypeToCivildrawObjectType(currentObject?.type),
        ...(currentMapId && { map_id: currentMapId }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
    const uploadRequests = accepted.map((file) => sendFile(file));

    return Promise.all(uploadRequests).then(() => {
      if (!isPermittingLocalMap(store.currentMapId ?? '')) {
        store.refreshMap();
      }

      const analyticsProject = project as unknown as Project;
      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawFileAdded, {
        project_id: analyticsProject.id,
        organization_id: analyticsProject.organization || undefined,
        object_type: mapObjectTypeToCivildrawObjectType(currentObject?.type),
        ...(currentMapId && { map_id: currentMapId }),
      });
    });
  };

  const acceptMap: Record<string, string[]> = {
    'image/*': imageExts.map((ext) => `.${ext}`),
    'video/*': videoExts.map((ext) => `.${ext}`),
    'application/octet-stream': Object.keys(fileIconClassMap).map((ext) => `.${ext}`),
  };
  const acceptFilesDisplay = Array.from(new Set(Object.values(acceptMap).flat())).join(', ');

  const content = (
    <div>
      <span className="inline text-2xs font-bold">Accepted File Types:&nbsp;</span>
      <span className="inline text-2xs">{acceptFilesDisplay}</span>
    </div>
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptMap,
    onDrop,
    maxSize: 1073741824, // max file size 1 GB
  });

  if (currentObject?.type === 'arrow' && currentObject?.properties.subtype === 'metadata') {
    return null;
  }

  return (
    <div className="my-6 w-full">
      <div className="flex items-center justify-between">
        <span className="text-base font-bold">Attachments</span>
        <div>
          <input {...getInputProps()} />
          <Tooltip asChild sideOffset={5} className="w-80" contentChildren={content}>
            <IconButton
              icon="plus"
              variant="secondary"
              aria-label="Add attachment"
              data-testid="civildraw-object-attachment-add"
              {...getRootProps({})}
            />
          </Tooltip>
        </div>
      </div>
      <FileView
        onDownloadFile={onDownloadFile}
        pendingDownloadId={pendingId}
        onDeleteFile={onDeleteFile}
        hasFile={hasProject}
        files={projectFiles}
        iconSize={iconSize}
        iconStrokeWidth={iconStrokeWidth}
      />
      <hr />
    </div>
  );
};
